import React from 'react';
import { Box, Typography, Stack, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getInheritedAccreditations } from '../utils/sectorUtils';

const AccreditationContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: '1rem',
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '0.75rem',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }
}));

const LogoContainer = styled(Box)({
  width: '100%',
  height: '170px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '0.5rem',
  padding: '0.25rem',
  backgroundColor: '#F8FAFC',
  borderRadius: '8px'
});

const AccreditationLogo = styled('img')({
  minWidth: '220px',
  minHeight: '165px',
  maxWidth: '95%',
  maxHeight: '95%',
  objectFit: 'contain',
  transform: 'scale(1.1)'
});

const InfoContainer = styled(Box)({
  width: '100%',
  textAlign: 'center'
});

const AccreditationInfo = styled(Typography)({
  color: '#475569',
  fontSize: '0.85rem',
  marginBottom: '0.25rem',
  lineHeight: 1.2,
  fontWeight: 500,
  '&:last-child': {
    marginBottom: 0
  }
});

const WebsiteLink = styled('a')({
  color: '#2563EB',
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: '0.85rem',
  '&:hover': {
    textDecoration: 'underline'
  }
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#1A365D',
  marginBottom: '1.5rem'
}));

const Accreditations = ({ company, activeSector }) => {
  const { accreditations } = getInheritedAccreditations(company, activeSector);

  const renderAccreditationContent = (accreditation) => {
    const { type, image, number, norme, website, link } = accreditation;

    return (
      <AccreditationContainer 
        elevation={0}
        onClick={() => link && window.open(link, '_blank', 'noopener noreferrer')}
        sx={{ cursor: link ? 'pointer' : 'default' }}
      >
        <LogoContainer>
          <AccreditationLogo 
            src={image} 
            alt={`${type} Logo`}
          />
        </LogoContainer>
        
        <InfoContainer>
          {type === 'COFRAC' && (
            <Stack spacing={1}>
              <AccreditationInfo>
                Accréditation N° : {number}
              </AccreditationInfo>
              <AccreditationInfo>
                Portée disponible sur :
                <br />
                <WebsiteLink 
                  href={`https://${website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {website}
                </WebsiteLink>
              </AccreditationInfo>
            </Stack>
          )}

          {type === 'SEMAC' && (
            <Stack spacing={1}>
              <AccreditationInfo>
                {number}
              </AccreditationInfo>
              <AccreditationInfo>
               {norme}
              </AccreditationInfo>
              <AccreditationInfo>
                Portée disponible sur :
                <br />
                <WebsiteLink 
                  href={`https://${website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {website}
                </WebsiteLink>
              </AccreditationInfo>
            </Stack>
          )}

          {type === 'TUNAC' && (
            <Stack spacing={1}>
              <AccreditationInfo>
                Portée disponible sur :
                <br />
                <WebsiteLink 
                  href={`https://${website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {website}
                </WebsiteLink>
              </AccreditationInfo>
            </Stack>
          )}
        </InfoContainer>
      </AccreditationContainer>
    );
  };

  return (
    <Box sx={{ position: 'sticky', top: '2rem' }}>
      <SectionTitle>
        Nos Accréditations
      </SectionTitle>
      <Stack spacing={2}>
        {accreditations.map((accreditation) => (
          renderAccreditationContent(accreditation)
        ))}
      </Stack>
      {accreditations.length === 0 && (
        <Typography 
          sx={{ 
            color: '#475569',
            textAlign: 'center',
            fontStyle: 'italic'
          }}
        >
          
        </Typography>
      )}
    </Box>
  );
};

export default Accreditations;
